import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { clampInspectorWidth, resolveMaxWidth, widthStorageKey } from "./inspectorModel.js";

export type InspectorFlyoutWidthConfig = {
  min: number;
  /** Fixed max, or a viewport-relative function evaluated on each resize. */
  max: number | ((viewportWidth: number) => number);
  default: number;
};

type UseInspectorFlyoutWidthArgs = InspectorFlyoutWidthConfig & {
  surfaceId: string;
  panelId: string;
};

/**
 * Owns a flyout's drag-resize + persisted width (ADR-0129 §3.1). Extracted from
 * the duplicated Atlas Query panel logic so every inspector shares one
 * implementation. Width persists to localStorage keyed per surface + panel.
 */
export function useInspectorFlyoutWidth(args: UseInspectorFlyoutWidthArgs) {
  const { surfaceId, panelId, min, max, default: defaultWidth } = args;
  const key = widthStorageKey(surfaceId, panelId);

  const [width, setWidth] = useState<number>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw != null) {
        const n = Number(raw);
        if (Number.isFinite(n)) {
          const vw = typeof window !== "undefined" ? window.innerWidth : Number.POSITIVE_INFINITY;
          return clampInspectorWidth(n, min, resolveMaxWidth(max, vw));
        }
      }
    } catch {
      /* localStorage unavailable — fall through to default */
    }
    return defaultWidth;
  });

  const widthRef = useRef(width);
  widthRef.current = width;
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onMove = useCallback(
    (e: PointerEvent) => {
      if (!resizeRef.current) return;
      const delta = resizeRef.current.startX - e.clientX;
      const vw = window.innerWidth;
      const next = clampInspectorWidth(resizeRef.current.startWidth + delta, min, resolveMaxWidth(max, vw));
      setWidth(next);
    },
    [min, max],
  );

  const stopResize = useCallback(() => {
    resizeRef.current = null;
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerup", stopResize);
    document.removeEventListener("pointercancel", stopResize);
    try {
      localStorage.setItem(key, String(widthRef.current));
    } catch {
      /* ignore persistence failure */
    }
  }, [onMove, key]);

  const startResize = useCallback(
    (e: ReactPointerEvent) => {
      e.preventDefault();
      resizeRef.current = { startX: e.clientX, startWidth: widthRef.current };
      setIsResizing(true);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", stopResize);
      document.addEventListener("pointercancel", stopResize);
    },
    [onMove, stopResize],
  );

  // Safety net: detach global listeners if the flyout unmounts mid-drag.
  useEffect(
    () => () => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", stopResize);
      document.removeEventListener("pointercancel", stopResize);
    },
    [onMove, stopResize],
  );

  return { width, isResizing, startResize };
}
